import { Spotlight, compactNumber, type SpotlightTile } from "@cntrl/ui";
import type { StatMover } from "../../app/hooks/useDashboardStats.js";
import { useDashboardStats } from "../../app/hooks/useDashboardStats.js";
import { platformLabel } from "./platforms.js";
import { useSpotlightPhoto } from "../../app/hooks/useSpotlightPhoto.js";
import styles from "./DashboardSpotlight.module.scss";

/** The three tile labels, shared across every state so the hero keeps a stable shape (born-blank shows `—`).
 *
 * The middle tile read "Views (30D)" over `spotify.monthlyListeners` — the MVP defect the reference's own
 * Ch.08 change request calls "wrong twice", and which we had shipped verbatim (audit 12.1): the label named a
 * metric we do not serve, and the value under it was a different metric again. Monthly listeners are people;
 * 30-day views are plays; neither is the other. The decision on the ticket is literal — *"the tile becomes
 * 'Spotify streams · 28D' fed by historic_stats → streams_28d. Kill the Views label and the monthly-listeners
 * feed here."* Note the two SPECIMENS still render the old label: they are a rendering of the shipped bug, and
 * the change request beside them is what supersedes it. */
const TILE_LABELS = ["Total followers", "Spotify streams · 28D", "Top mover"] as const;
const EMPTY_TILES: SpotlightTile[] = [
  { label: TILE_LABELS[0], value: null },
  { label: TILE_LABELS[1], value: null },
  { label: TILE_LABELS[2], value: null, small: true },
];

/**
 * The month's spotlight mover — the biggest POSITIVE mover. `trending.movers` is server-ranked by |delta%|
 * descending, so the first mover with a positive delta is the largest gain. Only ever a gain: Ryan's rule is
 * that the spotlight "shouldn't be anything negative, ever" (ART-199) — a down month is not a spotlight — so a
 * negative or flat top mover is skipped, and if nothing rose the card falls to its neutral born-blank state.
 * (The old BiggestMovers grid that once had to agree with movers[0] is retired, so there is no ordering to
 * honour beyond picking the top gain.)
 */
function topMover(movers: StatMover[]): StatMover | null {
  return movers.find((m) => m?.deltaPct != null && Number.isFinite(m.deltaPct) && m.deltaPct > 0) ?? null;
}

/** "+20.3%" / "−0.6%" — signed, one-decimal, tabular (mirrors StatDelta's rounding). */
function signedPct(v: number): string {
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? "+" : ""}${r}%`;
}

/**
 * The dashboard's Spotlight hero (cd-dashboard `.spotlight`) — the month's biggest mover, derived from the
 * SAME `useDashboardStats` read the analytics rows use (React Query dedupes; no extra request). Money-free by
 * construction. Every figure is null-degraded to `—` — the hero NEVER fabricates a number (born-blank +
 * leak-test): a fresh band renders the designed "Monthly Spotlight" empty state.
 */
export function DashboardSpotlight() {
  const q = useDashboardStats();
  const photo = useSpotlightPhoto();

  if (q.isLoading) {
    return <Spotlight title="" loading tiles={EMPTY_TILES} photoUrl={photo} />;
  }

  if (q.isError) {
    return (
      <Spotlight
        title="Monthly Spotlight"
        subtitle="We couldn't refresh your numbers just now."
        action={
          <button type="button" className={styles.retry} onClick={() => void q.refetch()}>
            Retry
          </button>
        }
        tiles={EMPTY_TILES}
        photoUrl={photo}
      />
    );
  }

  const data = q.data;
  const movers = data?.trending?.movers ?? [];
  const socials = data?.socials?.tiles ?? [];
  const spotify = data?.streaming?.spotify ?? null;
  const top = topMover(movers);

  // Total followers = Σ of every REAL follower count we hold — CONNECTED socials only (a disconnected tile with a
  // stale non-null count must not inflate the total: 06.4 disconnect leak test) + Spotify (its hero is only
  // populated when connected). Missing/all-null (born-blank) → null → "—". Never a fabricated total.
  const followerValues = [
    ...socials.filter((t) => t.connected).map((t) => t.followers),
    spotify?.followers,
  ].filter((n): n is number => n != null && Number.isFinite(n));
  const totalFollowers = followerValues.length ? followerValues.reduce((a, b) => a + b, 0) : null;
  // `streams28d` was already on the wire (SpotifyHero) — this tile never needed backend work, only the
  // right field.
  const streams28d = spotify?.streams28d ?? null;

  const topDelta = top?.deltaPct ?? null;
  const topDir = topDelta == null ? "flat" : topDelta > 0 ? "up" : topDelta < 0 ? "down" : "flat";
  const tiles: SpotlightTile[] = [
    { label: TILE_LABELS[0], value: totalFollowers != null ? compactNumber(totalFollowers) : null },
    { label: TILE_LABELS[1], value: streams28d != null ? compactNumber(streams28d) : null },
    {
      label: TILE_LABELS[2],
      small: true,
      value:
        top && topDelta != null ? (
          <>
            {platformLabel(top.platform)}{" "}
            <span className={styles.pos} data-dir={topDir}>
              {signedPct(topDelta)}
            </span>
          </>
        ) : null,
    },
  ];

  // `top` is the biggest POSITIVE mover or null (topMover skips flat/negative), so a spotlight is always a
  // gain — there is no "moved the most (down)" case any more (ART-199).
  const hasSpotlight = top != null && topDelta != null;
  const platform = platformLabel(top?.platform);
  // Only claim "grew across every channel" when EVERY mover is non-negative — otherwise it asserts growth the data
  // doesn't support (a channel may be falling while the #1 mover rose). spirit: no unsubstantiated claim.
  const allUp = hasSpotlight && movers.every((m) => (m.deltaPct ?? 0) >= 0);
  // The headline is FIXED "Monthly Spotlight" and only the subtext moves (Ryan, ART-199 — a dynamic
  // "{platform} is trending this month" headline just restated the subtext under it, "always redundant").
  const title = "Monthly Spotlight";
  const subtitle = !hasSpotlight
    ? "Your biggest platform moves each month show up here."
    : allUp
      ? `Your audience grew across every channel — ${platform} led the pack with the biggest 30-day lift.`
      : `${platform} led your platforms with the biggest 30-day move.`;

  // No top-right delta pill: it repeated the same percentage the "Top mover" tile already carries (Ryan,
  // ART-199 — "have percentage twice is redundant, kill the top right").
  return (
    <Spotlight
      title={title}
      subtitle={subtitle}
      tiles={tiles}
      photoUrl={photo}
    />
  );
}
